// Interactive blob — a wobbly path that bulges toward the cursor,
// with the user's profile photo clipped inside.

import { useEffect, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';

type BlobVibe = 'gooey' | 'springy' | 'liquid' | 'calm';

const VIBE_ORDER: readonly BlobVibe[] = ['gooey', 'springy', 'liquid', 'calm'];

interface BuildBlobArgs {
  cx: number;
  cy: number;
  baseR: number;
  t: number;
  mx: number;
  my: number;
  pull: number;
  wobble: number;
  speed: number;
  lobes: number;
  points?: number;
}

function buildBlobPath({
  cx,
  cy,
  baseR,
  t,
  mx,
  my,
  pull,
  wobble,
  speed,
  lobes,
  points = 96,
}: BuildBlobArgs): string {
  const pts: Array<[number, number]> = [];
  const angleToMouse = Math.atan2(my, mx);
  const mouseDist = Math.min(1, Math.hypot(mx, my));
  for (let i = 0; i < points; i++) {
    const a = (i / points) * Math.PI * 2;

    const w =
      Math.sin(a * lobes + t * speed) * 0.55 +
      Math.sin(a * (lobes + 2) - t * speed * 0.7) * 0.3 +
      Math.sin(a * (lobes + 5) + t * speed * 1.3) * 0.18;

    let da = a - angleToMouse;
    da = Math.atan2(Math.sin(da), Math.cos(da));
    const bulge = Math.exp(-(da * da) / 0.6) * pull * mouseDist;

    const pinch = -Math.exp(-((Math.PI - Math.abs(da)) ** 2) / 1.2) * pull * mouseDist * 0.35;

    const r = baseR * (1 + w * wobble + bulge * 0.32 + pinch);
    pts.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r]);
  }

  const first = pts[0];
  if (!first) return '';
  let d = `M ${first[0].toFixed(2)} ${first[1].toFixed(2)}`;
  for (let i = 0; i < pts.length; i++) {
    const p0 = pts[(i - 1 + pts.length) % pts.length]!;
    const p1 = pts[i]!;
    const p2 = pts[(i + 1) % pts.length]!;
    const p3 = pts[(i + 2) % pts.length]!;
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C ${c1x.toFixed(2)} ${c1y.toFixed(2)}, ${c2x.toFixed(2)} ${c2y.toFixed(2)}, ${p2[0].toFixed(2)} ${p2[1].toFixed(2)}`;
  }
  d += ' Z';
  return d;
}

const GITHUB_AVATAR = '/profile.jpg';

interface VibeConfig {
  wobble: number;
  speed: number;
  lobes: number;
  pull: number;
  follow: number;
  rot: number;
}

const VIBES: Record<BlobVibe, VibeConfig> = {
  gooey: { wobble: 0.08, speed: 0.6, lobes: 3, pull: 0.55, follow: 0.06, rot: 0.04 },
  springy: { wobble: 0.05, speed: 0.9, lobes: 4, pull: 0.7, follow: 0.18, rot: 0.05 },
  liquid: { wobble: 0.1, speed: 1.4, lobes: 5, pull: 0.45, follow: 0.1, rot: 0.18 },
  calm: { wobble: 0.04, speed: 0.4, lobes: 3, pull: 0.35, follow: 0.05, rot: 0.02 },
};

interface PointerState {
  x: number;
  y: number;
  active: number;
  rot: number;
}

interface InteractiveBlobProps {
  vibe?: BlobVibe;
  accent?: string;
}

function InteractiveBlob({
  vibe: initialVibe = 'gooey',
  accent = '#1f8a5b',
}: InteractiveBlobProps) {
  const [vibe, setVibe] = useState<BlobVibe>(initialVibe);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const pathRef = useRef<SVGPathElement | null>(null);
  const clipRef = useRef<SVGPathElement | null>(null);
  const ringRef = useRef<SVGGElement | null>(null);
  const ghostRef = useRef<SVGPathElement | null>(null);

  const target = useRef<{ x: number; y: number; active: number }>({ x: 0, y: 0, active: 0 });
  const current = useRef<PointerState>({ x: 0, y: 0, active: 0, rot: 0 });

  useEffect(() => {
    const cfg = VIBES[vibe] ?? VIBES.gooey;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let raf = 0;
    const start = performance.now() / 1000;

    const onMove = (e: MouseEvent) => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const r = wrap.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const dx = (e.clientX - cx) / (r.width / 2);
      const dy = (e.clientY - cy) / (r.height / 2);
      target.current.x = Math.max(-1.4, Math.min(1.4, dx));
      target.current.y = Math.max(-1.4, Math.min(1.4, dy));
      target.current.active = 1;
    };
    const onLeave = () => {
      target.current.active = 0;
      target.current.x *= 0.6;
      target.current.y *= 0.6;
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseleave', onLeave);

    const tick = (now: number) => {
      const t = now / 1000 - start;
      current.current.x += (target.current.x - current.current.x) * cfg.follow;
      current.current.y += (target.current.y - current.current.y) * cfg.follow;
      current.current.active += (target.current.active - current.current.active) * 0.08;
      current.current.rot += cfg.rot * 0.6;

      const cx = 250;
      const cy = 250;
      const baseR = 175;
      const d = buildBlobPath({
        cx,
        cy,
        baseR,
        t,
        mx: current.current.x,
        my: current.current.y,
        pull: cfg.pull * (0.4 + 0.6 * current.current.active),
        wobble: cfg.wobble,
        speed: cfg.speed,
        lobes: cfg.lobes,
      });

      if (pathRef.current) pathRef.current.setAttribute('d', d);
      if (clipRef.current) clipRef.current.setAttribute('d', d);
      if (ghostRef.current) {
        ghostRef.current.setAttribute('d', d);
        ghostRef.current.setAttribute(
          'transform',
          `rotate(${current.current.rot.toFixed(2)} ${cx} ${cy})`,
        );
      }
      if (ringRef.current) {
        const tx = current.current.x * 8;
        const ty = current.current.y * 8;
        ringRef.current.setAttribute('transform', `translate(${tx.toFixed(2)} ${ty.toFixed(2)})`);
      }

      if (!reducedMotion) raf = requestAnimationFrame(tick);
    };
    // With reduced motion we draw a single organic frame and hold still.
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseleave', onLeave);
    };
  }, [vibe]);

  const nextVibe = () =>
    setVibe((v) => VIBE_ORDER[(VIBE_ORDER.indexOf(v) + 1) % VIBE_ORDER.length]!);

  return (
    <div
      className="blob-stage"
      ref={wrapRef}
      onClick={nextVibe}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          nextVibe();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`Blob mood: ${vibe}. Activate to change.`}
    >
      <svg viewBox="0 0 500 500" aria-hidden="true">
        <defs>
          <clipPath id="blob-clip">
            <path ref={clipRef} d="" />
          </clipPath>
          <filter id="blob-shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="10" />
            <feOffset dx="0" dy="14" result="off" />
            <feComponentTransfer>
              <feFuncA type="linear" slope="0.18" />
            </feComponentTransfer>
            <feMerge>
              <feMergeNode />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <linearGradient id="blob-fill" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={accent} stopOpacity="0.9" />
            <stop offset="100%" stopColor={accent} stopOpacity="0.55" />
          </linearGradient>
        </defs>

        {/* outer halo ring (offset blob) */}
        <path
          ref={ghostRef}
          d=""
          fill="none"
          stroke={accent}
          strokeOpacity="0.18"
          strokeWidth="1.2"
          strokeDasharray="2 6"
        />

        {/* the blob itself — colored, holds the photo */}
        <g filter="url(#blob-shadow)">
          <path ref={pathRef} d="" fill="url(#blob-fill)" />
        </g>

        {/* photo clipped inside */}
        <g clipPath="url(#blob-clip)" ref={ringRef}>
          <rect x="0" y="0" width="500" height="500" fill="var(--bg-2)" />
          <image
            href={GITHUB_AVATAR}
            x="50"
            y="50"
            width="400"
            height="400"
            preserveAspectRatio="xMidYMid slice"
          />
        </g>

        {/* tiny crosshair tracking dot */}
        <CrosshairDot currentRef={current} />
      </svg>
      <div className="blob-hint">
        ↳ move your cursor · click to change mood — <em>{vibe}</em>
      </div>
    </div>
  );
}

interface CrosshairDotProps {
  currentRef: MutableRefObject<PointerState>;
}

function CrosshairDot({ currentRef }: CrosshairDotProps) {
  const ref = useRef<SVGCircleElement | null>(null);
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      if (ref.current) {
        const x = 250 + currentRef.current.x * 200;
        const y = 250 + currentRef.current.y * 200;
        ref.current.setAttribute('cx', String(x));
        ref.current.setAttribute('cy', String(y));
        ref.current.setAttribute('opacity', String(0.35 * currentRef.current.active));
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [currentRef]);
  return <circle ref={ref} cx="250" cy="250" r="3" fill="currentColor" opacity="0" />;
}

export default InteractiveBlob;
